import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Import the filter primitives DIRECTLY (not via the barrel) to keep
// the import graph explicit + tree-shake the heavier `<DateRangeFilter>`
// only where it's used (we do use it here — `dateFrom` / `dateTo`).
import { DateRangeFilter } from '../../../components/filters/DateRangeFilter';
import { FilterBar } from '../../../components/filters/FilterBar';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { STATUS_OPTIONS, type PayoutFilters } from '../_lib/payoutFilters';

/**
 * Filter toolbar for the payouts queue (AUT-128) — status multi-select +
 * inspector UUID free-text + date range. Extracted from the page so the
 * page stays under the line cap. Every value is URL-state-driven via the
 * parent's `setFilters` patch.
 *
 * NO search input (the backend query schema carries no `q` free-text) and
 * NO rail filter (not in the schema) — architect guardrail #2. The status
 * multi-select offers the THREE wire-stable statuses only.
 *
 * Inspector is a UUID free-text TextField (the backend's exact-match
 * `inspectorId` filter; a typeahead selector is a follow-up) — mirrors the
 * reports filter bar.
 */
export interface PayoutsFilterBarProps {
  filters: PayoutFilters;
  setFilters: (
    patch: Partial<Record<keyof PayoutFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const PayoutsFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: PayoutsFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('payouts.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <TextField
        size="small"
        label={t('payouts.filters.inspectorLabel')}
        placeholder={t('payouts.filters.inspectorPlaceholder')}
        value={filters.inspectorId ?? ''}
        onChange={(e) => setFilters({ inspectorId: e.target.value || null })}
        sx={{ minWidth: 200 }}
      />
      <DateRangeFilter
        from={filters.dateFrom ?? null}
        to={filters.dateTo ?? null}
        onChange={(range) => setFilters({ dateFrom: range.from, dateTo: range.to })}
      />
    </FilterBar>
  );
};
